/**
 * Hook to fetch annotation fields for signed_authorization_merge components.
 * Calls the validation endpoint to get all fields with their current values.
 */
import { useEffect, useMemo, useRef } from 'react';

import {
  PackageRequestInfo,
  useRequestPackageComponentValidateAllMutation,
} from '@/services/api/hipaaPackageService';

import type { AnnotationField } from './MissingFieldsEditor';

interface UseAnnotationFieldsOptions {
  componentId: string;
  packageId: string;
  requestInfo: PackageRequestInfo;
  /** Whether to skip fetching */
  skip?: boolean;
  /** Whether the field editor is currently shown - triggers fetch when true */
  isExpanded?: boolean;
}

interface UseAnnotationFieldsResult {
  annotationFields: AnnotationField[];
  isLoading: boolean;
}

export function useAnnotationFields({
  componentId,
  packageId,
  requestInfo,
  skip = false,
  isExpanded = false,
}: UseAnnotationFieldsOptions): UseAnnotationFieldsResult {
  const [validateAll, { data, isLoading }] =
    useRequestPackageComponentValidateAllMutation();

  // Track if we've already fetched to avoid duplicate calls
  const hasFetchedRef = useRef(false);

  // Fetch when expanded and not skipped
  useEffect(() => {
    if (isExpanded && !skip && packageId && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      validateAll({
        matterId: requestInfo.matterId,
        providerId: requestInfo.providerId,
        requestId: requestInfo.requestId,
        packageId,
      });
    }
    // Reset fetch flag when collapsed
    if (!isExpanded) {
      hasFetchedRef.current = false;
    }
  }, [
    isExpanded,
    skip,
    packageId,
    requestInfo.matterId,
    requestInfo.providerId,
    requestInfo.requestId,
    validateAll,
  ]);

  // Extract annotation fields for the specific component
  const annotationFields = useMemo(() => {
    if (!data?.components) return [];

    const componentValidation = data.components.find(
      (c) => c.component_id === componentId,
    );

    if (!componentValidation) return [];

    // Cast to access annotation_fields (types may not be generated yet)
    const fields = (
      componentValidation as unknown as { annotation_fields?: unknown[] }
    ).annotation_fields;

    if (!Array.isArray(fields)) return [];

    return fields as AnnotationField[];
  }, [data, componentId]);

  return {
    annotationFields,
    isLoading,
  };
}
